/**
 * MeetingsPage — where Teams meetings go into (or stay out of) the company
 * knowledge base.
 *
 * "Waiting for you": meetings whose transcript is ready and whose organizer
 * (the signed-in user) has not decided. Add = consent for Artie to read the
 * transcript and draft notes for review; nothing is shared until those notes
 * are approved. Not this one = declined for good.
 * "Review": drafted notes waiting on the organizer — open one to edit it beside
 * a PDF preview, then add it to the knowledge base or discard it
 * (MeetingReview). Notes being added stay listed until they are in.
 * "History": meetings already decided, newest first.
 *
 * Replaces the chat banner for Teams meetings (Granola keeps MeetingKbBanner).
 * Spec: docs/superpowers/specs/2026-10-06-teams-meeting-to-knowledge-base-design.md
 */

import * as React from "react";
import { Alert, Box, Button, Chip, CircularProgress, Paper, Tab, Tabs, Typography } from "@mui/material";
import { CalendarClock, FileText, History } from "lucide-react";
import {
  approveMeeting,
  listMeetingHistory,
  listReviewMeetings,
  pendingMeetingsSource,
  type MeetingHistoryItem,
  type MeetingStatus,
  type ReviewMeeting,
  type TeamsPendingMeeting,
} from "@/api/meetingsApi";
import { usePendingMeetings } from "@/chat/hooks/useGranolaPending";
import { useNotificationsOptional } from "@/notifications/NotificationsProvider";
import { PageHeader } from "../Common/PageHeader";
import { EmptyState } from "../Common/EmptyState";
import { CardSkeletonList } from "../Common/CardSkeleton";
import { Col } from "../Common/Row";
import { MeetingReview } from "./MeetingReview";

type MeetingsTab = "waiting" | "review" | "history";
// While notes are being added, the list re-checks until they are in.
const INDEXING_POLL_MS = 5000;

const STATUS_LABEL: Record<MeetingStatus, string> = {
  accepted: "Accepted",
  processing: "Drafting notes",
  awaiting_review: "Draft ready to review",
  indexing: "Adding to knowledge base",
  approved: "In knowledge base",
  declined: "Not added",
  expired: "Expired — no answer",
  failed: "Failed",
};

/** "Tue 6 Oct · 32 min · 5 people" — whatever parts are known. */
export function meetingDetail(m: TeamsPendingMeeting): string | null {
  const parts: string[] = [];
  if (m.occurred_at) {
    const when = new Date(m.occurred_at);
    if (!Number.isNaN(when.getTime())) {
      parts.push(
        when.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" }),
      );
    }
  }
  if (m.duration_seconds) parts.push(`${Math.max(1, Math.round(m.duration_seconds / 60))} min`);
  if (m.attendee_count) {
    parts.push(`${m.attendee_count} ${m.attendee_count === 1 ? "person" : "people"}`);
  }
  return parts.length ? parts.join(" · ") : null;
}

function PendingMeetingCard({
  meeting,
  onAdd,
  onDismiss,
}: {
  meeting: TeamsPendingMeeting;
  onAdd: () => Promise<void>;
  onDismiss: () => Promise<void>;
}) {
  const [busy, setBusy] = React.useState<"add" | "dismiss" | null>(null);
  const detail = meetingDetail(meeting);
  const run = (which: "add" | "dismiss", action: () => Promise<void>) => async () => {
    if (busy) return;
    setBusy(which);
    try {
      await action();
    } finally {
      setBusy(null);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
        {meeting.subject || "Untitled Teams meeting"}
      </Typography>
      {detail && (
        <Typography variant="body2" color="text.secondary">
          {detail}
        </Typography>
      )}
      <Typography variant="body2" sx={{ mt: 1 }}>
        Add this meeting to the company knowledge base?
      </Typography>
      <Typography variant="caption" color="text.secondary">
        Artie will read the transcript and draft notes for you to review. Nothing is shared
        until you approve.
      </Typography>
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 1.5 }}>
        <Button size="small" color="inherit" onClick={run("dismiss", onDismiss)} disabled={!!busy}>
          {busy === "dismiss" ? <CircularProgress size={13} /> : "Not this one"}
        </Button>
        <Button size="small" variant="contained" onClick={run("add", onAdd)} disabled={!!busy}>
          {busy === "add" ? <CircularProgress size={13} /> : "Add"}
        </Button>
      </Box>
    </Paper>
  );
}

function ReviewRow({
  item,
  onOpen,
  onRetry,
}: {
  item: ReviewMeeting;
  onOpen: () => void;
  onRetry: () => Promise<void>;
}) {
  const [retrying, setRetrying] = React.useState(false);
  const detail = [meetingDetail({ ...item, attendee_count: 0 }), `${item.item_count} items`]
    .filter(Boolean)
    .join(" · ");
  return (
    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, display: "flex", alignItems: "center", gap: 2 }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
          {item.subject || "Untitled Teams meeting"}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {detail}
        </Typography>
        {item.status === "awaiting_review" && item.last_error === "indexing_failed" && (
          <Typography variant="caption" color="warning.main" sx={{ display: "block" }}>
            Couldn't add last time. Open it to try again.
          </Typography>
        )}
      </Box>
      {item.status === "indexing" ? (
        item.stalled ? (
          <>
            <Typography variant="caption" color="text.secondary">
              Taking longer than expected
            </Typography>
            <Button
              size="small"
              disabled={retrying}
              onClick={async () => {
                setRetrying(true);
                try {
                  await onRetry();
                } finally {
                  setRetrying(false);
                }
              }}
            >
              Try again
            </Button>
          </>
        ) : (
          <Chip size="small" icon={<CircularProgress size={10} />} label={STATUS_LABEL.indexing} />
        )
      ) : (
        <Button size="small" variant="contained" onClick={onOpen}>
          Review
        </Button>
      )}
    </Paper>
  );
}

function HistoryRow({ item }: { item: MeetingHistoryItem }) {
  const detail = meetingDetail(item);
  const title = item.subject || (item.status === "declined" ? "Declined meeting" : "Untitled Teams meeting");
  return (
    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, display: "flex", alignItems: "center", gap: 2 }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
          {title}
        </Typography>
        {detail && (
          <Typography variant="caption" color="text.secondary">
            {detail}
          </Typography>
        )}
      </Box>
      <Chip size="small" label={STATUS_LABEL[item.status] ?? item.status} />
    </Paper>
  );
}

export function MeetingsPage() {
  const notifications = useNotificationsOptional();
  const pending = usePendingMeetings(pendingMeetingsSource);
  const [tab, setTab] = React.useState<MeetingsTab>("waiting");
  const [history, setHistory] = React.useState<MeetingHistoryItem[] | null>(null);
  const [historyError, setHistoryError] = React.useState<string | null>(null);
  const [reviews, setReviews] = React.useState<ReviewMeeting[] | null>(null);
  const [reviewError, setReviewError] = React.useState<string | null>(null);
  const [openReview, setOpenReview] = React.useState<ReviewMeeting | null>(null);
  const tabChosen = React.useRef(false);

  const loadHistory = React.useCallback(() => {
    setHistoryError(null);
    listMeetingHistory()
      .then(setHistory)
      .catch((e) => setHistoryError(e instanceof Error ? e.message : String(e)));
  }, []);

  const loadReviews = React.useCallback(() => {
    setReviewError(null);
    listReviewMeetings()
      .then(setReviews)
      .catch((e) => setReviewError(e instanceof Error ? e.message : String(e)));
  }, []);

  React.useEffect(() => {
    loadHistory();
    loadReviews();
  }, [loadHistory, loadReviews]);

  // Land on Review when drafts are ready and nothing is waiting (the "draft
  // ready" notification links here) — once, so it never fights the user.
  React.useEffect(() => {
    if (tabChosen.current || pending.loading || reviews === null) return;
    tabChosen.current = true;
    if (pending.meetings.length === 0 && reviews.some((r) => r.status === "awaiting_review")) {
      setTab("review");
    }
  }, [pending.loading, pending.meetings.length, reviews]);

  const indexing = reviews?.some((r) => r.status === "indexing" && !r.stalled) ?? false;
  React.useEffect(() => {
    if (!indexing) return;
    const timer = setInterval(() => {
      loadReviews();
      loadHistory();
    }, INDEXING_POLL_MS);
    return () => clearInterval(timer);
  }, [indexing, loadReviews, loadHistory]);

  const reviewDone = (meeting: ReviewMeeting) => (outcome: "approved" | "discarded") => {
    const name = meeting.subject || "Untitled Teams meeting";
    notifications?.notify(
      outcome === "approved"
        ? `Adding "${name}" to the knowledge base. You'll get a notification when it's there.`
        : `Discarded the draft for "${name}".`,
      "success",
    );
    setOpenReview(null);
    loadReviews();
    loadHistory();
  };

  const retryAdd = (meeting: ReviewMeeting) => async () => {
    try {
      await approveMeeting(meeting.id);
      loadReviews();
    } catch (exc) {
      notifications?.notify(
        exc instanceof Error ? exc.message : "Something went wrong. Try again.",
        "error",
      );
    }
  };

  const decide = (meeting: TeamsPendingMeeting, kind: "add" | "dismiss") => async () => {
    try {
      if (kind === "add") {
        await pending.accept(meeting.id);
        notifications?.notify(
          `Artie will draft notes from "${meeting.subject || "Untitled Teams meeting"}" for you to review.`,
          "success",
        );
      } else {
        await pending.dismiss(meeting.id);
      }
      loadHistory();
    } catch (exc) {
      notifications?.notify(
        exc instanceof Error ? exc.message : "Something went wrong. Try again.",
        "error",
      );
    }
  };

  const waitingCount = pending.meetings.length;
  const reviewCount = reviews?.filter((r) => r.status === "awaiting_review").length ?? 0;

  if (openReview) {
    return (
      <Box>
        <PageHeader title="Meetings" subtitle="Review the drafted notes, then add them to the knowledge base." />
        <MeetingReview
          meetingId={openReview.id}
          onBack={() => {
            setOpenReview(null);
            loadReviews();
          }}
          onDone={reviewDone(openReview)}
        />
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title="Meetings"
        subtitle="Teams meetings you organized, ready to add to the company knowledge base."
      />

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab value="waiting" label={`Waiting for you${waitingCount ? ` (${waitingCount})` : ""}`} />
        <Tab value="review" label={`Review${reviewCount ? ` (${reviewCount})` : ""}`} />
        <Tab value="history" label="History" />
      </Tabs>

      {tab === "waiting" && (
        <>
          {pending.error && waitingCount === 0 && (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              action={
                <Button color="inherit" size="small" onClick={pending.retry}>
                  Retry
                </Button>
              }
            >
              {pending.error}
            </Alert>
          )}
          {pending.loading && waitingCount === 0 && <CardSkeletonList />}
          {!pending.loading && !pending.error && waitingCount === 0 && (
            <EmptyState
              icon={CalendarClock}
              title="No meetings waiting for you"
              description="When a Teams meeting you organized has a transcript, it shows up here so you can choose whether it goes into the company knowledge base."
            />
          )}
          {waitingCount > 0 && (
            <Col gap={1.5}>
              {pending.meetings.map((m) => (
                <PendingMeetingCard
                  key={m.id}
                  meeting={m}
                  onAdd={decide(m, "add")}
                  onDismiss={decide(m, "dismiss")}
                />
              ))}
            </Col>
          )}
        </>
      )}

      {tab === "review" && (
        <>
          {reviewError && (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              action={
                <Button color="inherit" size="small" onClick={loadReviews}>
                  Retry
                </Button>
              }
            >
              {reviewError}
            </Alert>
          )}
          {!reviewError && reviews === null && <CardSkeletonList />}
          {reviews !== null && reviews.length === 0 && (
            <EmptyState
              icon={FileText}
              title="No drafts to review"
              description="When Artie has drafted notes from a meeting you added, they show up here for you to check and edit before they go into the knowledge base."
            />
          )}
          {reviews !== null && reviews.length > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
              Drafts left unreviewed for 30 days are deleted, along with what Artie kept from the
              transcript.
            </Typography>
          )}
          {reviews !== null && reviews.length > 0 && (
            <Col gap={1} data-testid="meeting-review-list">
              {reviews.map((r) => (
                <ReviewRow key={r.id} item={r} onOpen={() => setOpenReview(r)} onRetry={retryAdd(r)} />
              ))}
            </Col>
          )}
        </>
      )}

      {tab === "history" && (
        <>
          {historyError && (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              action={
                <Button color="inherit" size="small" onClick={loadHistory}>
                  Retry
                </Button>
              }
            >
              {historyError}
            </Alert>
          )}
          {!historyError && history === null && <CardSkeletonList />}
          {history !== null && history.length === 0 && (
            <EmptyState
              icon={History}
              title="No meeting history yet"
              description="Meetings you add, decline, or leave unanswered for 14 days are listed here."
            />
          )}
          {history !== null && history.length > 0 && (
            <Col gap={1} data-testid="meeting-history">
              {history.map((h) => (
                <HistoryRow key={h.id} item={h} />
              ))}
            </Col>
          )}
        </>
      )}
    </Box>
  );
}
